import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
// eslint-disable-next-line no-restricted-imports -- approved output-boundary shim for billboard consumers
import { List, Map } from 'immutable';
import moment from 'moment';

import { DEFAULT_DATETIME_FORMAT } from '@sophocles/app/constants';
import {
  addSitePhoto,
  deleteCameraImage,
  deleteSitePhoto,
  editSitePhoto,
  loadCameraImages,
  loadSitePhotos,
} from '@sophocles/features/media/api';
import { normalizeBinaryFileRow } from '@sophocles/features/media/normalize';

// file_type values as sent by the binaryfile socket payload
const FILE_TYPE_PHOTO = 'photo';
const FILE_TYPE_IMAGE = 'cic-image';

// Binary-file rows are keyed by guid (string), not numeric id.
const imageEntityAdapter = createEntityAdapter({ selectId: row => row.guid });
const photoEntityAdapter = createEntityAdapter({ selectId: row => row.guid });

const slice = createSlice({
  name: 'media',
  initialState: {
    images: imageEntityAdapter.getInitialState({}),
    photos: photoEntityAdapter.getInitialState({}),
    /**
     * Per-site summary seeded by the dashboard bootstrap thunk and kept live
     * by handleBinaryFileSocketActions. Shape per siteId:
     *   { photoCount: number, imageCount: number, recent: Array<{guid, file_url, file_type, timestamp}> }
     *
     * @type {{ [siteId: string]: { photoCount: number, imageCount: number, recent: object[] } }}
     */
    summaryBySite: {},
    view: {
      mode: 'list', // 'list' or 'grid'
      tab: 'photos', // 'images' or 'photos
      search: null,
      page: 0,
      order: 'asc',
      orderBy: 'id',
    },
  },
  reducers: {
    /**
     * Seed (or replace) the summary for a site from the bootstrap snapshot.
     *
     * @param {{ siteId: string|number, photoCount: number, imageCount: number, recent: object[] }} action.payload
     */
    setMediaSummary(state, action) {
      const { siteId, photoCount, imageCount, recent } = action.payload;
      state.summaryBySite[String(siteId)] = { photoCount, imageCount, recent: recent ?? [] };
    },
    setMode(state, action) {
      if (action.payload == 'grid' || action.payload == 'list') {
        state.view.mode = action.payload;
      }
    },
    setTab(state, action) {
      if (action.payload == 'images' || action.payload == 'photos') {
        state.view.tab = action.payload;
      }
    },
    setPage(state, action) {
      state.view.page = action.payload;
    },
    setSearch(state, action) {
      state.view.search = action.payload;
    },
    setOrder(state, action) {
      state.view.order = action.payload;
    },
    setOrderBy(state, action) {
      state.view.orderBy = action.payload;
    },
    _bumpPhotoCount(state, action) {
      const { siteId, item } = action.payload;
      const key = String(siteId);
      if (!state.summaryBySite[key]) return;
      const prev = state.summaryBySite[key];
      const recent = [item, ...prev.recent].slice(0, 4);
      state.summaryBySite[key] = { ...prev, photoCount: prev.photoCount + 1, recent };
    },
    _bumpImageCount(state, action) {
      const { siteId, item } = action.payload;
      const key = String(siteId);
      if (!state.summaryBySite[key]) return;
      const prev = state.summaryBySite[key];
      const recent = [item, ...prev.recent].slice(0, 4);
      state.summaryBySite[key] = { ...prev, imageCount: prev.imageCount + 1, recent };
    },
    // Update an existing recent[] entry in place when a binaryfile is edited
    // (post_save fires on edits too; created is falsy in that case).
    _updateRecentItem(state, action) {
      const { siteId, item } = action.payload;
      const key = String(siteId);
      if (!state.summaryBySite[key]) return;
      const prev = state.summaryBySite[key];
      const recent = prev.recent.map(r => (r.guid === item.guid ? item : r));
      state.summaryBySite[key] = { ...prev, recent };
    },
    _decrementPhotoCount(state, action) {
      const { siteId, guid } = action.payload;
      const key = String(siteId);
      if (!state.summaryBySite[key]) return;
      const prev = state.summaryBySite[key];
      const recent = prev.recent.filter(r => r.guid !== guid);
      state.summaryBySite[key] = { ...prev, photoCount: Math.max(0, prev.photoCount - 1), recent };
    },
    _decrementImageCount(state, action) {
      const { siteId, guid } = action.payload;
      const key = String(siteId);
      if (!state.summaryBySite[key]) return;
      const prev = state.summaryBySite[key];
      const recent = prev.recent.filter(r => r.guid !== guid);
      state.summaryBySite[key] = { ...prev, imageCount: Math.max(0, prev.imageCount - 1), recent };
    },
    /**
     * Record a camera image that arrived outside a fetch.
     *
     * The camera cards read captures from the entity store, which a fetch
     * seeds once per mount, so a capture that lands afterwards is only visible
     * to them once it is stored here too.
     *
     * @param {object} action.payload - a raw binary-file row
     */
    _upsertImageEntity(state, action) {
      imageEntityAdapter.upsertOne(state.images, normalizeBinaryFileRow(action.payload));
    },
    /**
     * Drop a camera image that was removed elsewhere.
     *
     * @param {string} action.payload - the image's guid
     */
    _removeImageEntity(state, action) {
      imageEntityAdapter.removeOne(state.images, action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(addSitePhoto.fulfilled, (state, action) => {
        const row = normalizeBinaryFileRow(action.payload);
        photoEntityAdapter.upsertOne(state.photos, row);
      })
      .addCase(editSitePhoto.fulfilled, (state, action) => {
        const row = normalizeBinaryFileRow(action.payload);
        photoEntityAdapter.upsertOne(state.photos, row);
      })
      .addCase(loadCameraImages.fulfilled, (state, action) => {
        imageEntityAdapter.upsertMany(state.images, action.payload.data ?? []);
      })
      .addCase(deleteCameraImage.fulfilled, (state, action) => {
        imageEntityAdapter.removeOne(state.images, action.payload);
      })
      .addCase(loadSitePhotos.fulfilled, (state, action) => {
        photoEntityAdapter.upsertMany(state.photos, action.payload.data ?? []);
      })
      .addCase(deleteSitePhoto.fulfilled, (state, action) => {
        photoEntityAdapter.removeOne(state.photos, action.payload);
      });
  },
});

export const selectMediaViewProperties = state => {
  return state.media.view;
};

export const selectPhotoEntities = state => {
  return state.media.photos;
};

export const { selectById: selectPhotoById } = photoEntityAdapter.getSelectors(selectPhotoEntities);

export const selectPhotoEntitiesWithMeta = createSelector(selectPhotoEntities, state => {
  return Object.keys(state.entities).map(key => {
    const row = state.entities[key];
    let uploader = null;
    if (row.uploaded_by != null) {
      uploader = row.uploaded_by.name || null;
    }
    return {
      ...row,
      meta: {
        title: null,
        description: row.comment,
        uploader,
        upload_time: row.timestamp ? format(parseISO(row.timestamp), DEFAULT_DATETIME_FORMAT) : null,
      },
    };
  });
});

/**
 * Select photos for a site, filtered by site_id.
 * Selector name kept stable for existing callers.
 */
export const selectPhotosBySiteId = siteId =>
  createSelector(selectPhotoEntitiesWithMeta, state => {
    return state.filter(item => String(item.site_id) === String(siteId));
  });

export const selectImageEntities = state => {
  return state.media.images;
};

export const { setMode: setImagesMode, setTab: setImagesTab, setMediaSummary } = slice.actions;

// Stable reference so the selector returns the same object each call when no
// summary exists, instead of a fresh {} that triggers needless re-renders.
const EMPTY_MEDIA_SUMMARY = Object.freeze({ photoCount: 0, imageCount: 0, recent: Object.freeze([]) });

/**
 * Select the media summary for a site, or a zeroed default when not yet seeded.
 *
 * @param {string|number} siteId
 * @returns {function} selector returning { photoCount, imageCount, recent }
 */
export const selectMediaSummaryBySite = siteId => state =>
  state.media.summaryBySite[String(siteId)] ?? EMPTY_MEDIA_SUMMARY;

/**
 * Returns an Immutable Map keyed by String(camera_id) → Immutable List of image Maps.
 * Each image Map carries a moment-parsed `timestamp` field, matching the shape
 * that the legacy getCICImageToCameraMapping selector returned so that existing
 * consumers (CameraCard, cameraSelectors) need no shape changes.
 *
 * @returns {function} selector returning Immutable.Map<string, Immutable.List<Immutable.Map>>
 */
// DEPRECATED(immutable-freeze-shims): billboard-only shim; drop once views/sites/components/SiteDashboard/CameraCard.jsx and entities/Camera/cameraSelectors.js stop consuming the Immutable shape.
export const selectImagesByCamera = createSelector(selectImageEntities, imageState => {
  let mapping = Map();
  Object.values(imageState.entities).forEach(image => {
    if (!image) return;
    const cameraKey = String(image.camera ?? image.actor?.id ?? -1);
    const imageMap = Map(image).set('timestamp', moment(image.timestamp));
    const list = mapping.get(cameraKey, List());
    mapping = mapping.set(cameraKey, list.push(imageMap));
  });
  return mapping;
});

/**
 * Socket handler for the 'binaryfile' model.
 *
 * Drives summaryBySite (counts + recent thumbnails) for every file, and keeps
 * the image entity store in step so the camera cards show a capture as it
 * lands rather than on their next mount. Photo rows stay summary-only; the
 * photo grid refetches on next navigation.
 *
 * Signature matches getModelHandler outputs: (op, data) => dispatch => void.
 *
 * '+' with data.created === true: increment count + prepend to recent (capped at 4).
 * '+' with data.created falsy (edit): update the matching recent[] entry in place;
 *   do NOT change counts.
 * '-': decrement the matching count; remove the matching guid from recent.
 *
 * If summaryBySite has no entry for the site, the event is a no-op (dashboard
 * not yet bootstrapped for that site).
 *
 * @param {'+' | '-'} op
 * @param {{ guid: string, file_type: string, entity_id: number, file_url: string, timestamp: string, created?: boolean }} data
 */
/**
 * Socket handler for the 'media_summary' contract on the dashboard stream.
 *
 * The dashboard reads the site's media from this contract, which carries the
 * counts, the most-recent files, and the camera that produced each one. The
 * camera cards read captures from the image store, so recent captures are
 * recorded there too and a card shows one the moment it lands.
 *
 * @param {'+'|'-'} _op - unused; the contract is always a full replace
 * @param {{photo_count: number, image_count: number, recent: object[]}} payload
 * @param {{siteId: number|string}} meta - envelope metadata
 */
export const handleMediaSummaryContractSocketActions = (_op, payload, meta) => dispatch => {
  const siteId = meta?.siteId;
  if (!payload || siteId == null) return;

  const recent = payload.recent ?? [];
  dispatch(
    setMediaSummary({
      siteId,
      photoCount: payload.photo_count ?? 0,
      imageCount: payload.image_count ?? 0,
      recent,
    }),
  );

  recent
    .filter(item => item.file_type === FILE_TYPE_IMAGE)
    .forEach(item => dispatch(slice.actions._upsertImageEntity(item)));
};

export const handleBinaryFileSocketActions = (op, data) => dispatch => {
  if (!data || !data.guid || data.entity_id == null) return;

  const siteId = String(data.entity_id);

  if (op === '+') {
    if (data.created) {
      if (data.file_type === FILE_TYPE_PHOTO) {
        dispatch(slice.actions._bumpPhotoCount({ siteId, item: data }));
      } else if (data.file_type === FILE_TYPE_IMAGE) {
        dispatch(slice.actions._bumpImageCount({ siteId, item: data }));
        dispatch(slice.actions._upsertImageEntity(data));
      }
    } else {
      dispatch(slice.actions._updateRecentItem({ siteId, item: data }));
      if (data.file_type === FILE_TYPE_IMAGE) {
        dispatch(slice.actions._upsertImageEntity(data));
      }
    }
  } else if (op === '-') {
    if (data.file_type === FILE_TYPE_PHOTO) {
      dispatch(slice.actions._decrementPhotoCount({ siteId, guid: data.guid }));
    } else if (data.file_type === FILE_TYPE_IMAGE) {
      dispatch(slice.actions._decrementImageCount({ siteId, guid: data.guid }));
      dispatch(slice.actions._removeImageEntity(data.guid));
    }
  }
};

const { reducer } = slice;
export default reducer;
